import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { onOpenAgeDisclaimerPopup } from "@/components/popup-dispatcher/PopupDispatcher";
import { useUserStore } from "@/common/store/userStore";
import { loadAgeDisclaimerAcceptedAt, loadRedditAppVisit } from "@/common/utils/storage";
import { useScrollStore } from "@/common/store/scrollStore";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { popupBus } from "@/common/buses";
import type { IOpenedPopupEvent } from "@/common/buses";
import { PopupsEnum } from "@/components/popup-dispatcher/PopupDispatcher/popups";

// eslint-disable-next-line no-magic-numbers
const FOUR_WEEKS_IN_MS = 1000 * 60 * 60 * 24 * 7 * 4;

export const useAgeDisclaimer = () => {
  const { pathname } = useLocation();
  const currentUser = useUserStore((state) => state.currentUser);
  const initialPathRef = useRef(pathname);
  const scrollElement = useScrollStore((state) => state.scrollElement);
  const eventSource = useAnalyticsStore((state) => state.eventSource);
  const [blurredOff, setBlurredOff] = useState<boolean>(false);

  const initialPath = initialPathRef.current;

  const shouldShowAgeDisclaimer = useMemo(() => {
    if (currentUser?.verified) {
      return false;
    }

    if (initialPath.startsWith("/ifr/")) {
      return false;
    }

    const isRedditVisit = loadRedditAppVisit();

    if (isRedditVisit) {
      return false;
    }

    const acceptedAt = loadAgeDisclaimerAcceptedAt();
    const isOutsideAllowedPeriod = typeof acceptedAt !== "number" || Date.now() - acceptedAt >= FOUR_WEEKS_IN_MS;

    return isOutsideAllowedPeriod;
  }, [currentUser, eventSource, initialPath]);

  const shouldBlurApp = !blurredOff && (currentUser === undefined || shouldShowAgeDisclaimer);

  useEffect(() => {
    if (!scrollElement) {
      return;
    }

    scrollElement.classList.toggle("blurred", shouldBlurApp);
  }, [scrollElement, shouldBlurApp]);

  useEffect(() => {
    if (!shouldShowAgeDisclaimer) {
      return;
    }

    onOpenAgeDisclaimerPopup();
  }, [shouldShowAgeDisclaimer]);

  useEffect(() => {
    const handlePopupOpened = (event: IOpenedPopupEvent) => {
      if (event.name === PopupsEnum.AgeDisclaimer) {
        setBlurredOff(true);
      }
    };

    popupBus.on("opened", handlePopupOpened);

    return () => {
      popupBus.off("opened", handlePopupOpened);
    };
  }, []);
};
